import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  CalendarDays,
  CalendarIcon,
  DollarSign,
  FileDown,
  XCircle,
  X,
  AlertTriangle,
  Pencil,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  reservado: 'bg-blue-100 text-blue-800',
  liquidado: 'bg-green-100 text-green-800',
  cancelado: 'bg-red-100 text-red-800',
  completado: 'bg-purple-100 text-purple-800',
};

const initialFormData = {
  cliente_id: '',
  cliente_nombre: '',
  fecha: format(new Date(), 'yyyy-MM-dd'),
  hora_inicio: '10:00',
  hora_fin: '16:00',
  direccion: '',
  detalles: [],
  inflable_id: '',
  incluye_transporte: false,
  costo_transporte: 0,
  tipo_descuento: null,
  valor_descuento: 0,
  monto_descuento: 0,
  subtotal: 0,
  total: 0,
  anticipo: 0,
  saldo_pendiente: 0,
  forma_pago: 'efectivo',
  estatus: 'reservado',
  notas: '',
};

export default function RentasAdminPage() {
  const { getAuthHeader, user } = useAuth();
  const [rentas, setRentas] = useState([]);
  const [inflables, setInflables] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pagoDialogOpen, setPagoDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedRenta, setSelectedRenta] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [pagoMonto, setPagoMonto] = useState(0);
  const [pagoForma, setPagoForma] = useState('efectivo');
  const [cancelMotivo, setCancelMotivo] = useState('cliente');
  const [realizarReembolso, setRealizarReembolso] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedInflable, setSelectedInflable] = useState('');
  const [editingRenta, setEditingRenta] = useState(null);
  const fetchData = async () => {
    try {
      const [rentasRes, infRes, cliRes] = await Promise.all([
        axios.get(`${API_URL}/rentas`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/inflables?activo=true`),
        axios.get(`${API_URL}/clientes`, { headers: getAuthHeader() }),
      ]);
      setRentas(rentasRes.data);
      setInflables(infRes.data);
      setClientes(cliRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const recalculateTotals = (data) => {
    const subtotalInflables = data.detalles.reduce((sum, det) => sum + det.subtotal_item, 0);
    const subtotal = subtotalInflables + (data.incluye_transporte ? data.costo_transporte : 0);
    
    let monto_descuento = 0;
    if (data.tipo_descuento === 'porcentaje' && data.valor_descuento > 0) {
      monto_descuento = subtotal * (data.valor_descuento / 100);
    } else if (data.tipo_descuento === 'monto' && data.valor_descuento > 0) {
      monto_descuento = data.valor_descuento;
    }
    
    const total = Math.max(0, subtotal - monto_descuento);
    const saldo_pendiente = total - data.anticipo;
    
    return { ...data, subtotal, monto_descuento, total, saldo_pendiente };
  };

  const handleAddInflable = () => {
    if (!selectedInflable) return;
    const inflable = inflables.find(i => i.id === selectedInflable);
    if (!inflable) return;
    
    if (formData.detalles.some(d => d.inflable_id === selectedInflable)) {
      toast.error('Este inflable ya está agregado');
      return;
    }
    
    const newDetalle = {
      inflable_id: inflable.id,
      inflable_nombre: inflable.nombre,
      precio_unitario: inflable.precio_base,
      horas_extra: 0,
      precio_hora_extra: inflable.precio_hora_extra,
      subtotal_item: inflable.precio_base,
    };
    
    const newDetalles = [...formData.detalles, newDetalle];
    const updated = recalculateTotals({ ...formData, detalles: newDetalles });
    setFormData(updated);
    setSelectedInflable('');
  };

  const handleRemoveInflable = (inflableId) => {
    const newDetalles = formData.detalles.filter(d => d.inflable_id !== inflableId);
    const updated = recalculateTotals({ ...formData, detalles: newDetalles });
    setFormData(updated);
  };

  const handleUpdateDetalle = (inflableId, field, value) => {
    const newDetalles = formData.detalles.map(det => {
      if (det.inflable_id === inflableId) {
        const updated = { ...det, [field]: value };
        if (field === 'horas_extra') {
          updated.subtotal_item = updated.precio_unitario + (value * updated.precio_hora_extra);
        }
        return updated;
      }
      return det;
    });
    const updated = recalculateTotals({ ...formData, detalles: newDetalles });
    setFormData(updated);
  };

  const handleFieldChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    const updated = recalculateTotals(newData);
    setFormData(updated);
  };

  const handleOpenDialog = (renta = null) => {
    if (renta) {
      setEditingRenta(renta);
      setFormData({
        ...renta,
        incluye_transporte: renta.incluye_transporte || false,
        costo_transporte: renta.costo_transporte || 0,
        tipo_descuento: renta.tipo_descuento || 'monto',
        valor_descuento: renta.valor_descuento || 0,
        monto_descuento: renta.monto_descuento || 0,
        forma_pago: renta.forma_pago || 'efectivo',
      });
      setSelectedDate(new Date(renta.fecha + 'T12:00:00'));
    } else {
      setEditingRenta(null);
      setFormData(initialFormData);
      setSelectedDate(new Date());
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.detalles.length === 0) {
      toast.error('Agrega al menos un inflable');
      return;
    }
    
    if (!formData.cliente_id) {
      toast.error('Selecciona un cliente');
      return;
    }
    
    try {
      if (editingRenta) {
        await axios.put(`${API_URL}/rentas/${editingRenta.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Renta actualizada');
      } else {
        await axios.post(`${API_URL}/rentas`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Renta creada exitosamente');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al guardar renta');
    }
  };

  const handleDeleteRenta = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta renta?')) return;
    try {
      await axios.delete(`${API_URL}/rentas/${id}`, { headers: getAuthHeader() });
      toast.success('Renta eliminada');
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al eliminar');
    }
  };

  const handleOpenPagoDialog = (renta) => {
    setSelectedRenta(renta);
    setPagoMonto(renta.saldo_pendiente);
    setPagoDialogOpen(true);
  };

  const handleRegistrarPago = async () => {
    if (!selectedRenta) return;
    try {
      await axios.post(`${API_URL}/rentas/${selectedRenta.id}/pago`, {
        monto: pagoMonto,
        forma_pago: pagoForma,
      }, { headers: getAuthHeader() });
      toast.success('Pago registrado');
      setPagoDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al registrar pago');
    }
  };

  const handleOpenCancelDialog = (renta) => {
    setSelectedRenta(renta);
    setCancelMotivo('cliente');
    setRealizarReembolso(false);
    setCancelDialogOpen(true);
  };

  const handleCancelarRenta = async () => {
    if (!selectedRenta) return;
    try {
      await axios.post(`${API_URL}/rentas/${selectedRenta.id}/cancelar`, {
        motivo: cancelMotivo,
        realizar_reembolso: realizarReembolso,
      }, { headers: getAuthHeader() });
      toast.success('Renta cancelada');
      setCancelDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al cancelar');
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await axios.get(`${API_URL}/rentas/${id}/pdf`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `renta_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  const getInflableNames = (renta) => {
    if (renta.detalles && renta.detalles.length > 0) {
      return renta.detalles.map(d => d.inflable_nombre || 'Inflable').join(', ');
    }
    const inf = inflables.find(i => i.id === renta.inflable_id);
    return inf?.nombre || '-';
  };

  const getClienteName = (id) => clientes.find(c => c.id === id)?.nombre || 'Sin cliente';

  const filteredRentas = rentas.filter(r =>
    r.folio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="rentas-admin-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-3xl font-bold text-allegra-dark">Rentas</h1>
          <p className="text-xs sm:text-sm text-allegra-gray">Gestiona las reservas de inflables</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="rounded-full gap-1.5 h-9 text-xs sm:text-sm sm:h-10" data-testid="add-renta-btn">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Nueva Renta</span>
          <span className="sm:hidden">Nueva</span>
        </Button>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2 sm:pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-allegra-gray" />
            <Input
              placeholder="Buscar por folio o cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-xl text-sm"
              data-testid="search-rentas"
            />
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : filteredRentas.length === 0 ? (
            <div className="text-center py-12">
              <CalendarDays className="w-10 h-10 text-allegra-gray/50 mx-auto mb-3" />
              <p className="text-allegra-gray text-sm">No hay rentas registradas</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-2">
                {filteredRentas.map((renta) => (
                  <div key={renta.id} className={`p-3 bg-muted/50 rounded-xl ${renta.estatus === 'cancelado' ? 'opacity-60' : ''}`} data-testid={`renta-card-${renta.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-allegra-gray">{renta.folio}</span>
                          <Badge className={`text-[10px] h-5 ${statusColors[renta.estatus]}`}>{renta.estatus}</Badge>
                          {renta.reembolso_realizado && <Badge className="text-[10px] h-5 bg-purple-100 text-purple-800">Reemb.</Badge>}
                        </div>
                        <p className="font-medium text-sm text-allegra-dark mt-0.5">{renta.cliente_nombre || getClienteName(renta.cliente_id)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-allegra-gray mb-1 truncate" title={getInflableNames(renta)}>{getInflableNames(renta)}</p>
                    <div className="flex justify-between items-center text-xs text-allegra-gray mb-2">
                      <span>{format(new Date(renta.fecha + 'T12:00:00'), 'd MMM yyyy', { locale: es })} - {renta.hora_inicio}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-bold text-allegra-dark">Total: ${renta.total?.toLocaleString()}</span>
                      <span className={`text-xs font-bold ${renta.saldo_pendiente > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {renta.saldo_pendiente > 0 ? `Debe: $${renta.saldo_pendiente?.toLocaleString()}` : 'Liquidado'}
                      </span>
                    </div>
                    {renta.notas && <p className="text-[10px] text-allegra-gray mb-2 truncate" title={renta.notas}>{renta.notas}</p>}
                    <div className="flex gap-1.5 flex-wrap">
                      <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => handleDownloadPDF(renta.id)} data-testid={`pdf-renta-${renta.id}`}>
                        <FileDown className="w-3 h-3" /> PDF
                      </Button>
                      {renta.saldo_pendiente > 0 && renta.estatus !== 'cancelado' && (
                        <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => handleOpenPagoDialog(renta)} data-testid={`pago-renta-${renta.id}`}>
                          <DollarSign className="w-3 h-3" /> Pago
                        </Button>
                      )}
                      {renta.estatus !== 'cancelado' && renta.estatus !== 'completado' && (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleOpenDialog(renta)} data-testid={`edit-renta-${renta.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {renta.estatus !== 'cancelado' && renta.estatus !== 'completado' && (
                        <Button variant="outline" size="sm" className="h-7 text-xs text-red-500" onClick={() => handleOpenCancelDialog(renta)} data-testid={`cancel-renta-${renta.id}`}>
                          <XCircle className="w-3 h-3" />
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="h-7 text-xs text-red-500" onClick={() => handleDeleteRenta(renta.id)} data-testid={`delete-renta-${renta.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Folio</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Cliente</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Inflables</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Fecha</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Total</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Pendiente</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Estatus</th>
                      <th className="text-right py-3 px-3 text-sm font-medium text-allegra-gray">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRentas.map((renta) => (
                      <tr key={renta.id} className={`border-b last:border-0 ${renta.estatus === 'cancelado' ? 'opacity-60' : ''}`} data-testid={`renta-row-${renta.id}`}>
                        <td className="py-3 px-3 font-mono text-sm">{renta.folio}</td>
                        <td className="py-3 px-3 text-sm">{renta.cliente_nombre || getClienteName(renta.cliente_id)}</td>
                        <td className="py-3 px-3 text-sm max-w-[150px] truncate" title={getInflableNames(renta)}>{getInflableNames(renta)}</td>
                        <td className="py-3 px-3 text-sm">
                          {format(new Date(renta.fecha + 'T12:00:00'), 'd MMM yyyy', { locale: es })}
                          <br /><span className="text-xs text-allegra-gray">{renta.hora_inicio}</span>
                        </td>
                        <td className="py-3 px-3 text-sm font-bold">${renta.total?.toLocaleString()}</td>
                        <td className={`py-3 px-3 text-sm font-bold ${renta.saldo_pendiente > 0 ? 'text-amber-600' : 'text-green-600'}`}>${renta.saldo_pendiente?.toLocaleString()}</td>
                        <td className="py-3 px-3">
                          <Badge className={statusColors[renta.estatus]}>{renta.estatus}</Badge>
                          {renta.reembolso_realizado && <Badge className="ml-1 bg-purple-100 text-purple-800">Reembolsado</Badge>}
                        </td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex justify-end gap-1.5 flex-wrap">
                            <Button variant="outline" size="sm" onClick={() => handleDownloadPDF(renta.id)} data-testid={`pdf-renta-${renta.id}`}><FileDown className="w-4 h-4" /></Button>
                            {renta.saldo_pendiente > 0 && renta.estatus !== 'cancelado' && (
                              <Button variant="outline" size="sm" className="gap-1" onClick={() => handleOpenPagoDialog(renta)} data-testid={`pago-renta-${renta.id}`}><DollarSign className="w-4 h-4" />Pago</Button>
                            )}
                            {renta.estatus !== 'cancelado' && renta.estatus !== 'completado' && (
                              <Button variant="outline" size="sm" onClick={() => handleOpenDialog(renta)} data-testid={`edit-renta-${renta.id}`}><Pencil className="w-4 h-4" /></Button>
                            )}
                            {renta.estatus !== 'cancelado' && renta.estatus !== 'completado' && (
                              <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleOpenCancelDialog(renta)} data-testid={`cancel-renta-${renta.id}`}><XCircle className="w-4 h-4" /></Button>
                            )}
                            <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDeleteRenta(renta.id)} data-testid={`delete-renta-${renta.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Nueva Renta Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingRenta ? 'Editar Renta' : 'Nueva Renta'}</DialogTitle>
            <DialogDescription>
              {editingRenta ? 'Modifica los datos de la renta' : 'Completa los datos para crear una nueva renta'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Cliente */}
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select value={formData.cliente_id} onValueChange={(v) => {
                const cliente = clientes.find(c => c.id === v);
                setFormData({ ...formData, cliente_id: v, cliente_nombre: cliente?.nombre || '' });
              }}>
                <SelectTrigger data-testid="renta-cliente">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fecha */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start font-normal text-xs sm:text-sm">
                      <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                      {format(selectedDate, "d MMM", { locale: es })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        if (date) {
                          setSelectedDate(date);
                          setFormData({ ...formData, fecha: format(date, 'yyyy-MM-dd') });
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Hora Inicio</Label>
                <Input type="time" value={formData.hora_inicio} onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Hora Fin</Label>
                <Input type="time" value={formData.hora_fin} onChange={(e) => setFormData({ ...formData, hora_fin: e.target.value })} />
              </div>
            </div>

            {/* Dirección */}
            <div className="space-y-2">
              <Label>Dirección *</Label>
              <Input
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                required
                data-testid="renta-direccion"
              />
            </div>

            {/* Agregar Inflables */}
            <div className="space-y-2">
              <Label>Agregar Inflables *</Label>
              <div className="flex gap-2">
                <Select value={selectedInflable} onValueChange={setSelectedInflable}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Seleccionar inflable" />
                  </SelectTrigger>
                  <SelectContent>
                    {inflables.filter(i => !formData.detalles.some(d => d.inflable_id === i.id)).map((i) => (
                      <SelectItem key={i.id} value={i.id}>{i.nombre} - ${i.precio_base}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={handleAddInflable} disabled={!selectedInflable}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Lista de Inflables */}
            {formData.detalles.length > 0 && (
              <div className="space-y-2 border rounded-xl p-3">
                {formData.detalles.map((det) => (
                  <div key={det.inflable_id} className="flex items-center justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{det.inflable_nombre}</p>
                      <p className="text-xs text-allegra-gray">${det.precio_unitario}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-allegra-gray">+Hrs:</span>
                        <Input type="number" min={0} value={det.horas_extra} onChange={(e) => handleUpdateDetalle(det.inflable_id, 'horas_extra', Number(e.target.value))} className="w-16 h-8 text-sm" />
                      </div>
                      <span className="font-bold text-sm whitespace-nowrap">${det.subtotal_item}</span>
                      <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleRemoveInflable(det.inflable_id)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Transporte y Descuento */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Transporte</Label>
                <Input
                  type="number"
                  min={0}
                  value={formData.costo_transporte}
                  onChange={(e) => handleFieldChange('costo_transporte', Number(e.target.value))}
                  onFocus={() => setFormData({ ...formData, incluye_transporte: true })}
                />
              </div>
              <div className="space-y-2">
                <Label>Descuento</Label>
                <div className="flex gap-2">
                  <Select value={formData.tipo_descuento || 'none'} onValueChange={(v) => handleFieldChange('tipo_descuento', v === 'none' ? null : v)}>
                    <SelectTrigger className="w-28">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin</SelectItem>
                      <SelectItem value="monto">$</SelectItem>
                      <SelectItem value="porcentaje">%</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    min={0}
                    value={formData.valor_descuento}
                    onChange={(e) => handleFieldChange('valor_descuento', Number(e.target.value))}
                    disabled={!formData.tipo_descuento}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {/* Anticipo y Forma de Pago */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Anticipo Recibido</Label>
                <Input
                  type="number"
                  min={0}
                  max={formData.total}
                  value={formData.anticipo}
                  onChange={(e) => handleFieldChange('anticipo', Number(e.target.value))}
                  data-testid="renta-anticipo"
                />
              </div>
              <div className="space-y-2">
                <Label>Forma de Pago</Label>
                <Select value={formData.forma_pago} onValueChange={(v) => setFormData({ ...formData, forma_pago: v })}>
                  <SelectTrigger data-testid="renta-forma-pago">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notas / Especificaciones */}
            <div className="space-y-2">
              <Label>Notas / Especificaciones del Cliente</Label>
              <textarea
                className="flex min-h-[80px] w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={formData.notas || ''}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Ej: Llevar extensión eléctrica, inflable para patio trasero, cliente pide montaje a las 9am..."
                data-testid="renta-notas"
              />
            </div>

            {/* Summary */}
            <div className="bg-muted rounded-xl p-4 space-y-2">
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span className="text-primary">${formData.total.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-allegra-gray">Anticipo:</span>
                <span className="text-green-600">${formData.anticipo.toLocaleString()}</span>
              </div>
              <div className="flex justify-between font-bold border-t pt-2">
                <span>Saldo Pendiente:</span>
                <span className="text-amber-600">${formData.saldo_pendiente.toLocaleString()}</span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={formData.detalles.length === 0 || !formData.cliente_id} data-testid="save-renta-btn">
                {editingRenta ? 'Guardar Cambios' : 'Crear Renta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pago Dialog */}
      <Dialog open={pagoDialogOpen} onOpenChange={setPagoDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar Pago</DialogTitle>
            <DialogDescription>Registra un pago para esta renta</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted rounded-xl p-4">
              <p className="text-sm text-allegra-gray">Folio: <strong>{selectedRenta?.folio}</strong></p>
              <p className="text-sm text-allegra-gray">Saldo pendiente: <strong className="text-amber-600">${selectedRenta?.saldo_pendiente?.toLocaleString()}</strong></p>
            </div>
            <div className="space-y-2">
              <Label>Monto a pagar</Label>
              <Input
                type="number"
                min={1}
                max={selectedRenta?.saldo_pendiente}
                value={pagoMonto}
                onChange={(e) => setPagoMonto(Number(e.target.value))}
                data-testid="pago-monto"
              />
            </div>
            <div className="space-y-2">
              <Label>Forma de pago</Label>
              <Select value={pagoForma} onValueChange={setPagoForma}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="efectivo">Efectivo</SelectItem>
                  <SelectItem value="transferencia">Transferencia</SelectItem>
                  <SelectItem value="tarjeta">Tarjeta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPagoDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleRegistrarPago} data-testid="confirm-pago-btn">Registrar Pago</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Cancelar Renta
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. La renta {selectedRenta?.folio} será marcada como cancelada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Motivo de cancelación</Label>
              <Select value={cancelMotivo} onValueChange={setCancelMotivo}>
                <SelectTrigger data-testid="cancel-motivo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cliente">Cancelación por el Cliente</SelectItem>
                  <SelectItem value="empresa">Cancelación por la Empresa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {cancelMotivo === 'empresa' && selectedRenta?.anticipo > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-amber-800">¿Realizar reembolso?</p>
                    <p className="text-sm text-amber-600">Anticipo: ${selectedRenta?.anticipo?.toLocaleString()}</p>
                  </div>
                  <Switch
                    checked={realizarReembolso}
                    onCheckedChange={setRealizarReembolso}
                    data-testid="reembolso-switch"
                  />
                </div>
                {realizarReembolso && (
                  <p className="mt-2 text-sm text-amber-700">
                    Se registrará un egreso de ${selectedRenta?.anticipo?.toLocaleString()} en finanzas.
                  </p>
                )}
              </div>
            )}
            
            {cancelMotivo === 'cliente' && selectedRenta?.anticipo > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-700">
                  El anticipo de ${selectedRenta?.anticipo?.toLocaleString()} quedará retenido.
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleCancelarRenta} data-testid="confirm-cancel-btn">
                Confirmar Cancelación
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
